import { t } from "i18next";
import { dynamicArray, isNot } from "neetocist";
import { dayjs } from "neetocommons/utils";
import { isEditorEmpty } from "neetoeditor";
import Papa from "papaparse";
import { prop } from "ramda";
import { string } from "yup";
import * as yup from "yup";

import { INITIAL_VALUES } from "./constants";

const isValidEmail = email => string().email().isValidSync(email);

export const isPastDate = date =>
  dayjs(date).isBefore(dayjs().subtract(1, "days"));

export const createEmailOptions = (emails = []) =>
  emails.map(email => ({
    label: email,
    value: email,
    valid: isValidEmail(email),
  }));

export const parseCsvEmails = file =>
  new Promise(resolve => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: h => h.toLowerCase().replace(/\W/g, ""),
      complete: results => {
        const isEmailFieldPresent = results.meta.fields?.includes("email");
        if (!isEmailFieldPresent) return resolve([]);

        const emails = results.data
          .map(item => item.email?.trim())
          .filter(isNot(undefined));

        return resolve(createEmailOptions(emails));
      },
    });
  });

export const generateInitialValues = defaultValues => ({
  ...INITIAL_VALUES,
  ...defaultValues,
});

export const emailFormValidationSchema = disableReplyTo =>
  yup.object().shape({
    emailSubject: yup
      .string()
      .required(
        t("neetoMolecules.shareViaEmail.validations.requiredField.subject")
      ),
    emailBody: yup
      .string()
      .test(
        "emailBody",
        t("neetoMolecules.shareViaEmail.validations.requiredField.emailBody"),
        value => !isEditorEmpty(value)
      ),
    sendFromEmail: yup
      .string()
      .trim()
      .email(
        t("neetoMolecules.shareViaEmail.validations.invalidField.emailAddress")
      )
      .required(
        t("neetoMolecules.shareViaEmail.validations.requiredField.sendFrom")
      ),
    replyToEmail:
      !disableReplyTo &&
      yup
        .string()
        .trim()
        .email(
          t(
            "neetoMolecules.shareViaEmail.validations.invalidField.emailAddress"
          )
        )
        .required(
          t("neetoMolecules.shareViaEmail.validations.requiredField.replyTo")
        ),
    notifyEmails: yup
      .array()
      .min(
        1,
        t(
          "neetoMolecules.shareViaEmail.validations.atleastOneIsRequired.emailAddress"
        )
      )
      .test(
        "are-all-emails-valid",
        t("neetoMolecules.shareViaEmail.validations.invalidField.emailAddress"),
        emails => emails.every(prop("valid"))
      ),
    isSendLaterEnabled: yup.boolean(),
    sendEmailDatetime: yup.date().when("isSendLaterEnabled", {
      is: true,
      then: yup
        .date()
        .required(
          t("neetoMolecules.shareViaEmail.validations.requiredField.sendLater")
        ),
    }),
    name: yup.string(),
    actionButtonText: yup
      .string()
      .required(
        t(
          "neetoMolecules.shareViaEmail.validations.requiredField.startButtonText"
        )
      ),
  });

export const disabledTime = selectedDate => {
  const current = dayjs(selectedDate).startOf("day");
  const today = dayjs().startOf("day");

  if (current.isBefore(today)) {
    return {
      disabledHours: () => dynamicArray(24, i => i),
      disabledMinutes: () => dynamicArray(60, i => i),
    };
  }

  if (current.isSame(today)) {
    const currentTime = dayjs();
    const currentHour = currentTime.hour();
    const currentMinute = currentTime.minute();

    return {
      disabledHours: () => dynamicArray(currentHour, i => i),
      disabledMinutes: selectedHour =>
        selectedHour === currentHour ? dynamicArray(currentMinute, i => i) : [],
    };
  }

  return {};
};
